import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const TestimonialCarousel = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { isDark } = useTheme();

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'Senior Product Manager',
      company: 'TechFlow Solutions',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=faces',
      content: `Lahiru's technical expertise and problem-solving abilities are exceptional. He delivered our e-commerce platform ahead of schedule with innovative features that exceeded our expectations.\n\nHis attention to detail and ability to translate complex requirements into elegant solutions made him an invaluable team member.`,
      rating: 5,
      project: 'E-Commerce Platform',duration: '6 months'
    },
    {
      id: 2,
      name: 'Michael Rodriguez',role: 'CTO',company: 'StartupHub Inc.',avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=faces',
      content: `Working with Lahiru was a game-changer for our startup. His full-stack expertise helped us build a scalable architecture that grew with our business.\n\nHe's not just a developer but a strategic thinker who understands business needs and translates them into technical solutions.`,
      rating: 5,
      project: 'SaaS Platform',
      duration: '8 months'
    },
    {
      id: 3,
      name: 'Emily Watson',
      role: 'Lead Designer',
      company: 'Creative Digital Agency',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=faces',
      content: `Lahiru brings designs to life with pixel-perfect precision. His collaboration skills and technical knowledge make the design-to-development handoff seamless.\n\nHe often suggests improvements that enhance both user experience and technical performance.`,
      rating: 5,
      project: 'Portfolio Website',
      duration: '3 months'
    },
    {
      id: 4,
      name: 'David Kim',
      role: 'Engineering Manager',
      company: 'DataTech Corp',
      avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=faces',
      content: `Lahiru's expertise in data visualization and analytics helped us create dashboards that transformed how our clients understand their business metrics.\n\nHis code quality is outstanding, and he consistently delivers maintainable, well-documented solutions.`,
      rating: 5,
      project: 'Analytics Dashboard',duration: '4 months'
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <DarkModeWrapper sectionName="testimonial">
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Client Testimonials
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Trusted by clients and collaborators who value quality, innovation, and reliable delivery
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Main Testimonial */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                  className={`rounded-2xl p-8 lg:p-12 shadow-premium border testimonial-card ${isDark ? 'border-gray-700' : 'border-border'}`}
                >
                  {/* Quote Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Quote" size={24} className="text-primary" />
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex justify-center space-x-1 mb-6">
                    {renderStars(testimonials[currentTestimonial].rating)}
                  </div>

                  {/* Testimonial Content */}
                  <blockquote className="text-lg lg:text-xl text-text-primary text-center leading-relaxed mb-8 whitespace-pre-line">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-primary/20">
                        <Image
                          src={testimonials[currentTestimonial].avatar}
                          alt={testimonials[currentTestimonial].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-white flex items-center justify-center">
                        <Icon name="Check" size={12} className="text-white" />
                      </div>
                    </div>

                    {/* Author Details */}
                    <div className="text-center sm:text-left">
                      <h4 className="text-xl font-semibold text-text-primary">
                        {testimonials[currentTestimonial].name}
                      </h4>
                      <p className="text-text-secondary">
                        {testimonials[currentTestimonial].role}
                      </p>
                      <p className="text-primary font-medium">
                        {testimonials[currentTestimonial].company}
                      </p>
                    </div>

                    {/* Project Info */}
                    <div className="hidden lg:block w-px h-16 bg-border"></div>
                    <div className="text-center sm:text-left">
                      <p className="text-sm text-text-secondary">Project</p>
                      <p className="font-medium text-text-primary">
                        {testimonials[currentTestimonial].project}
                      </p>
                      <p className="text-sm text-text-secondary">
                        Duration: {testimonials[currentTestimonial].duration}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button
                onClick={prevTestimonial}
                className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full shadow-premium border flex items-center justify-center testimonial-navigation ${isDark ? 'bg-slate-800 border-gray-700 hover:bg-slate-700' : 'bg-white border-border hover:bg-surface'} transition-premium`}
                aria-label="Previous testimonial"
              >
                <Icon name="ChevronLeft" size={20} className="text-text-primary" />
              </button>

              <button
                onClick={nextTestimonial}
                className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full shadow-premium border flex items-center justify-center testimonial-navigation ${isDark ? 'bg-slate-800 border-gray-700 hover:bg-slate-700' : 'bg-white border-border hover:bg-surface'} transition-premium`}
                aria-label="Next testimonial"
              >
                <Icon name="ChevronRight" size={20} className="text-text-primary" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-premium ${
                    index === currentTestimonial
                      ? 'bg-primary' : isDark ? 'bg-gray-700 hover:bg-primary/50' : 'bg-border hover:bg-primary/50'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Auto-play Control */}
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-premium"
              >
                <Icon 
                  name={isAutoPlaying ? 'Pause' : 'Play'} 
                  size={16} 
                />
                <span>{isAutoPlaying ? 'Pause' : 'Play'} Auto-rotation</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {[
                { label: 'Projects Completed', value: '25+', icon: 'CheckCircle' },
                { label: 'Client Satisfaction', value: '98%', icon: 'Heart' },
                { label: 'On-Time Delivery', value: '100%', icon: 'Clock' },
                { label: 'Long-term Partnerships', value: '15+', icon: 'Users' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name={stat.icon} size={20} className="text-primary" />
                  </div>
                  <p className="text-2xl font-bold text-text-primary">{stat.value}</p>
                  <p className="text-sm text-text-secondary">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default TestimonialCarousel;